import * as React from 'react';

import {
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior,
  DialogProps,
  DialogType,
  DialogWidget,
  InteractionState
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { Telemetry } from '../../ts/services/TelemetryService';
import { ServerUrls } from '../ServerUrls';
import { ConnectedNavPanel } from './ConnectedNavPanel';
import { NavStyles } from './NavigationStyles';
import { NavPanelButton } from './NavPanelButton';
import { PageHeaderStyles } from './PageHeaderStyles';

export const ActiveIcon = imgPath('spaces_toolbar_menu_active.svg');
export const Icon = imgPath('spaces_toolbar_menu.svg');

export interface PageHeaderNavPanelButtonProps {
  readonly serverUrls: ServerUrls;
  readonly telemetryService: Telemetry;
}

export interface PageHeaderNavPanelButtonState {
  showingNavPanel: boolean;
  startShowing: boolean;
  startHiding: boolean;
}

export class PageHeaderNavPanelButton extends React.PureComponent<PageHeaderNavPanelButtonProps, PageHeaderNavPanelButtonState> {

  private hideNavigationTimeoutId: any = null;
  public constructor(props: PageHeaderNavPanelButtonProps) {
    super(props);

    this.state = { showingNavPanel: false, startShowing: false, startHiding: false };
  }

  public render(): JSX.Element {
    return (
      <>
        <ButtonWidget
          buttonType={ButtonType.Icon}
          getContent={this.getButtonContent}
          title={this.isPanelOpen() ? Messages.leftNavToggleModalInvisible() : Messages.leftNavToggleModalVisible()}
          testId={'nav-panel-modal'}
          handleClick={this.toggleNavPanelVisibility}
        />
        {this.state.showingNavPanel && this.modalNavPanel()}
      </>
    );
  }

  public componentWillUnmount() {
    if (this.hideNavigationTimeoutId) {
      clearTimeout(this.hideNavigationTimeoutId);
    }
  }

  private getButtonContent = (interactionState: InteractionState): JSX.Element => {
    const useActiveImg = this.isPanelOpen() || interactionState.focus || interactionState.hover;
    return(
      <div className={PageHeaderStyles.NavPanelButton} >
        <img
          alt={this.isPanelOpen() ? Messages.leftNavToggleModalInvisible() : Messages.leftNavToggleModalVisible()}
          style={{height: 24, width: 24}}
          src={useActiveImg ? ActiveIcon : Icon}
        />
      </div>
    );
  }

  private toggleNavPanelVisibility = () => {

    this.props.telemetryService.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'modal-left-nav',
      action: this.state.showingNavPanel ? 'close' : 'open'
    });

    if (!this.state.showingNavPanel) {
      this.setState({showingNavPanel: true, startShowing: true});
    } else {
      this.startHidingNavPanel();
    }
  }

  private startHidingNavPanel = () => {
    this.setState({startHiding: true});
    this.hideNavigationTimeoutId = setTimeout(() => {
      this.setState({showingNavPanel: false, startHiding: false});
      this.hideNavigationTimeoutId = null;
    }, NavStyles.NavPanelFlyoutHideAnimationTime);
  }

  private hideNavPanel = () => {
    this.setState({showingNavPanel: false, startHiding: false});
  }

  private isPanelOpen() {
    return this.state.showingNavPanel;
  }

  public componentDidUpdate() {
    if (this.state.startShowing) {
      this.setState({startShowing: false});
    }
  }

  private modalNavPanel = (): JSX.Element => {
    const dialogStyle: React.CSSProperties = {
      padding: 0,
      boxShadow: 'none',
      borderWidth: '0px',
      position: 'fixed',
      backgroundColor: '', // get rid of the default dialog background color
      top: 0,
      left: 0,
      bottom: 0,
      // Shared Widgets uses something called ViewportConstrainedDom that breaks this dialog by automatically setting
      // the max height of the Dialog to some height that is smaller than the viewport.
      maxHeight: '',
    };

    const dialogContentStyle: React.CSSProperties = {
      padding: 0,
      height: '100%',
    };

    const dialogBodyStyle: React.CSSProperties = {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      height: '100%'
    };

    const props: DialogProps & React.HTMLAttributes<HTMLDivElement> = {
        testId: 'modal-nav-panel',
        showCloseButton: false,
        type: DialogType.Uber,
        onCloseRequest: this.startHidingNavPanel,
        isModal: true,
        isGlassTransparent: false,
        handleGlassClick: this.startHidingNavPanel,
        showFooter: false,
        positioningBehavior: DialogPositioningBehavior.Anchored,
        dialogStyle,
        dialogContentStyle,
        dialogBodyStyle,
        title: Messages.navigationPanelTitle()
      };

    return (
      <DialogWidget {...props}>
        <div className={NavStyles.getNavPanelFlyoutContainer(this.state.startShowing || this.state.startHiding)}>
          <div style={{display: 'flex'}} className={NavStyles.getLeftNavClassNames('expanded')}>
            <NavPanelButton
              panelState={'expanded'}
              toggleNavPanel={this.startHidingNavPanel}
              panelOpenMessage={Messages.leftNavToggleButtonInvisible()}
              telemetryService={this.props.telemetryService}
            />
            <ConnectedNavPanel
              navPanelDisplayMode={'iconAndText'}
              serverUrls={this.props.serverUrls}
              onSelectionMade={this.hideNavPanel}
              telemetryService={this.props.telemetryService}
            />
          </div>
        </div>
      </DialogWidget>
    );
  }
}
